<template>
  <!--
    P4 AI Tutor WP-03-CHAT-CORRECTION-R3 (doc 11) -- renders a tutor reply's
    Markdown + LaTeX. `v-html` is used ONLY with the string produced by
    `renderMarkdown` (markdown-it with raw HTML disabled + KaTeX with
    `trust: false`, http/https/mailto links only -- see markdownRenderer.js);
    the model's text is never bound to `v-html` directly.

    FINAL CHAT V2 -- a reply may carry figures (upstream 0a347ba, design A15)
    as fenced ```figure blocks whose body is a JSON spec. Those blocks are cut
    out of the text before rendering and drawn by TutorFigure (plain
    components, no v-html); the text around them goes through the same
    renderer as before. A reply without figures renders exactly as it did.

    CHAT-UX-01 (doc 29 §4.2) -- `profile="student"` renders a student's own
    message: the same renderer and the same safety, minus what belongs to the
    tutor. A ```figure fence in a student's text is NOT a figure (it is never
    cut out, so it renders as the ordinary code block it is and no
    TutorFigure is ever built from student text); `*` between two operands
    stays the multiplication sign the student typed; and the colours suit
    the student bubble. The default profile is the tutor's, unchanged. A
    student's container has a test id of its own, so `ai-tutor-markdown`
    still means what it always meant: text the tutor side produced.

    CHAT-UX-01 delta (doc 29 §17.3 D-CX-7, D-CX-9) -- the student profile also
    typesets the math a student wrote WITHOUT `$…$` (`x^2 + 5y = z`), for
    display only (implicitMath.js); and `inline` renders one line with no
    block element and nothing clickable in it (a suggestion card's label),
    in a <span> of its own test id, so a positional count of tutor replies
    is not disturbed by a label.

    AT-API-020-H3 (doc 34/03 §7) -- a tutor reply's `[^n]` citation markers
    (Tutor API 0.2.0) are drawn by the renderer as small numbers, with the
    matching source line as their title (`sources`); the student profile and
    the inline mode never do this. The sources list itself is the bubble's.

    POST-UAT-UX / PUX-A (doc 35/01 §1) -- a marker whose source has a page
    image (`source.image`) is a button; activating it emits `open-citation`
    with the marker's number. The URL never enters the rendered HTML.
  -->
  <span
    v-if="inline"
    class="ai-md ai-md--inline"
    :class="{ 'ai-md--student': isStudent }"
    data-testid="ai-tutor-inline-markdown"
    v-html="inlineHtml"
  />
  <div
    v-else-if="!hasFigures"
    class="ai-md"
    :class="{ 'ai-md--student': isStudent }"
    :data-testid="isStudent ? 'ai-tutor-student-markdown' : 'ai-tutor-markdown'"
    @click="onClick"
    v-html="html"
  />
  <div v-else class="ai-md" data-testid="ai-tutor-markdown" @click="onClick">
    <template v-for="(segment, index) in segments">
      <tutor-figure
        v-if="segment.type === 'figure'"
        :key="`f${index}`"
        :spec="segment.spec"
      />
      <div
        v-else
        :key="`t${index}`"
        class="ai-md__part"
        v-html="segment.html"
      />
    </template>
  </div>
</template>

<script>
import { renderMarkdown, renderMarkdownInline } from './markdownRenderer'
import { splitFigureBlocks, resolveFigure } from './figures/figureBlocks'
import TutorFigure from './figures/TutorFigure.vue'
import { sourceLine } from './sourceLine'

const MATH_WIDE = 'ai-md__math--wide'

export default {
  name: 'MarkdownContent',
  components: { TutorFigure },
  props: {
    source: {
      type: String,
      default: '',
    },
    // The turn's stored figure specs: a book figure's signed image URL is
    // only there, not in the reply text.
    figures: {
      type: Array,
      default: () => [],
    },
    // AT-API-020-H3: the turn's sources (the `citations` of a 0.2.0 reply, as
    // the adapter maps them, [{n, title, page, item}]): their lines become the
    // `title` of the `[^n]` markers in the text. The list itself is the
    // bubble's (ChatSources).
    sources: {
      type: Array,
      default: () => [],
    },
    // CHAT-UX-01: whose text this is. 'student' = no tutor-only transform.
    profile: {
      type: String,
      default: 'tutor',
      validator: value => ['tutor', 'student'].includes(value),
    },
    // CHAT-UX-01 delta: one line, no block element, nothing clickable.
    inline: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isStudent() {
      return this.profile === 'student'
    },
    rendererOptions() {
      if (this.isStudent) {
        return { literalIntrawordAsterisk: true, implicitMath: true }
      }
      // AT-API-020-H3: a tutor reply's `[^n]` markers are small numbers; in
      // the inline mode (a label) there is nothing to cite.
      if (this.inline) return undefined
      const options = {
        citationMarkers: true,
        citationTitles: this.citationTitles,
      }
      // PUX-A: only when a source has a page image -- otherwise the options
      // (and so every marker) are exactly H3's.
      if (this.citationActions) options.citationActions = this.citationActions
      return options
    },
    // PUX-A: number -> «صفحهٔ منبع n را باز کن» for the sources the turn lists
    // with a page image; null when there is none.
    citationActions() {
      const t = (key, values) =>
        typeof this.$t === 'function' ? this.$t(key, values) : key
      const actions = {}
      let any = false
      this.sources.forEach(source => {
        if (!source || !Number.isInteger(source.n) || source.n < 1) return
        if (actions[source.n] || typeof source.image !== 'string') return
        if (!source.image) return
        actions[source.n] = t('aiTutorSetup.chat.sources.open', { n: source.n })
        any = true
      })
      return any ? actions : null
    },
    // AT-API-020-H3: number -> «منبع n: the source line», for the markers'
    // titles; only the numbers the turn lists, the first entry for each.
    citationTitles() {
      const t = (key, values) =>
        typeof this.$t === 'function' ? this.$t(key, values) : key
      const titles = {}
      this.sources.forEach(source => {
        if (!source || !Number.isInteger(source.n) || source.n < 1) return
        if (titles[source.n]) return
        titles[source.n] = t('aiTutorSetup.chat.sources.marker', {
          n: source.n,
          source: sourceLine(source, t),
        })
      })
      return titles
    },
    inlineHtml() {
      return this.inline
        ? renderMarkdownInline(this.source, this.rendererOptions)
        : ''
    },
    parts() {
      // A student's text is one piece of text, whatever it contains.
      if (this.isStudent) {
        return typeof this.source === 'string' && this.source !== ''
          ? [{ type: 'text', text: this.source }]
          : []
      }
      return splitFigureBlocks(this.source)
    },
    hasFigures() {
      return this.parts.some(part => part.type === 'figure')
    },
    html() {
      return this.hasFigures
        ? ''
        : renderMarkdown(
            this.parts.length ? this.parts[0].text : '',
            this.rendererOptions
          )
    },
    segments() {
      return this.parts.map(part =>
        part.type === 'figure'
          ? { type: 'figure', spec: resolveFigure(part.spec, this.figures) }
          : {
              type: 'text',
              html: renderMarkdown(part.text, this.rendererOptions),
            }
      )
    },
  },
  mounted() {
    this.fitMath()
    // BT-65: the bubble's width changes with the window (rotation, a side
    // panel); the formulas are measured again then.
    if (this.inline) return
    if (typeof window === 'undefined') return
    // Measured in the next frame, never inside the observer's own callback (a
    // class change there would be a ResizeObserver loop).
    this.onMathResize = () => {
      if (this.mathFrame || !window.requestAnimationFrame) return
      this.mathFrame = window.requestAnimationFrame(() => {
        this.mathFrame = 0
        this.fitMath()
      })
    }
    if (window.ResizeObserver) {
      this.mathResizeObserver = new window.ResizeObserver(this.onMathResize)
      this.mathResizeObserver.observe(this.$el)
    } else {
      window.addEventListener('resize', this.onMathResize)
    }
    // A formula measured in a fallback font grows when the KaTeX fonts arrive,
    // with no resize and no re-render to tell: measured again then (review P3).
    const fonts = typeof document !== 'undefined' && document.fonts
    if (fonts && typeof fonts.addEventListener === 'function') {
      fonts.addEventListener('loadingdone', this.onMathResize)
      this.mathFonts = fonts
      if (fonts.ready && typeof fonts.ready.then === 'function') {
        fonts.ready.then(() => {
          if (!this._isDestroyed && this.onMathResize) this.onMathResize()
        })
      }
    }
  },
  // A streamed reply re-renders its HTML for every chunk: new formulas.
  updated() {
    this.fitMath()
  },
  beforeDestroy() {
    if (this.mathResizeObserver) this.mathResizeObserver.disconnect()
    else if (this.onMathResize) {
      window.removeEventListener('resize', this.onMathResize)
    }
    if (this.mathFonts) {
      this.mathFonts.removeEventListener('loadingdone', this.onMathResize)
    }
    if (this.mathFrame && window.cancelAnimationFrame) {
      window.cancelAnimationFrame(this.mathFrame)
    }
  },
  methods: {
    // BT-65 (P41): an inline formula wider than the whole text column is the
    // only one that becomes its own sideways-scrolling box (MATH_WIDE); every
    // other formula stays an ordinary inline run in one piece. The formula's
    // own width is read from its `.katex` run, which a scroll box never clips.
    // The column is the formula's own block (a list item or a quote is
    // narrower than the reply), never wider than the reply itself.
    fitMath() {
      const root = this.$el
      if (this.inline || !root || typeof root.querySelectorAll !== 'function') {
        return
      }
      const rootWidth = root.clientWidth
      if (!rootWidth) return
      root.querySelectorAll('.ai-md__math').forEach(math => {
        let block = math.parentElement
        // an inline parent (strong, a link) has no clientWidth of its own
        while (block && block !== root && !block.clientWidth) {
          block = block.parentElement
        }
        const available =
          block && block !== root && block.clientWidth
            ? Math.min(rootWidth, block.clientWidth)
            : rootWidth
        const run = math.querySelector('.katex') || math
        const width = run.getBoundingClientRect().width
        const wide = width > available + 0.5
        if (math.classList.contains(MATH_WIDE) !== wide) {
          math.classList.toggle(MATH_WIDE, wide)
        }
      })
    },
    // PUX-A: one delegated listener for the rendered marker buttons (a
    // keyboard Enter / Space on a button is a click too).
    onClick(event) {
      if (this.isStudent || this.inline || !this.citationActions) return
      const target = event && event.target
      const button =
        target && typeof target.closest === 'function'
          ? target.closest('[data-cite-open]')
          : null
      if (!button || !this.$el.contains(button)) return
      const n = Number(button.getAttribute('data-cite-open'))
      if (!Number.isInteger(n) || n < 1 || !this.citationActions[n]) return
      event.preventDefault()
      this.$emit('open-citation', { n, opener: button })
    },
  },
}
</script>

<!-- KaTeX's own stylesheet (and its fonts, which webpack emits). Every selector
     in it is namespaced under `.katex`; it changes no global typography. -->
<style>
@import '~katex/dist/katex.min.css';
</style>

<style lang="scss" scoped>
// Typography for a reply inside the 12px tutor bubble. Paragraph spacing
// follows the reference console (`.bubble p { margin: 0.3em 0 }`); everything
// else is sized to the prototype's chat scale so a heading never out-shouts the
// 20px page title. The HTML is injected with v-html, so every rule reaches it
// through `::v-deep`.
$ai-tutor-ink: $borna-ink;
$ai-tutor-indigo: $borna-indigo-600;
$ai-tutor-indigo-50: $borna-indigo-50;
$ai-tutor-indigo-100: $borna-indigo-100;
$ai-tutor-muted: $borna-muted;
$ai-tutor-border: $borna-border;
$ai-tutor-code-font: ui-monospace, Menlo, Consolas, monospace;

.ai-md {
  min-width: 0;
  overflow-wrap: break-word;

  // Neither a leading nor a trailing gap: the bubble's own padding frames it.
  > ::v-deep :first-child {
    margin-top: 0;
  }

  > ::v-deep :last-child {
    margin-bottom: 0;
  }

  ::v-deep {
    p {
      margin: 0.3em 0;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin: 0.9em 0 0.3em;
      color: $ai-tutor-ink;
      font-weight: 800;
      line-height: 1.6;
    }

    h1 {
      font-size: 15px;
    }

    h2 {
      font-size: 14px;
    }

    h3 {
      font-size: 13px;
    }

    h4,
    h5,
    h6 {
      font-size: 12px;
    }

    ul,
    ol {
      margin: 0.4em 0;
      padding-inline-start: 22px;
    }

    li {
      margin: 0.15em 0;

      > ul,
      > ol {
        margin: 0.15em 0;
      }
    }

    blockquote {
      margin: 0.5em 0;
      padding: 2px 12px;
      border-inline-start: 3px solid $ai-tutor-indigo-100;
      color: $ai-tutor-muted;
    }

    hr {
      height: 1px;
      margin: 0.8em 0;
      border: 0;
      background: $ai-tutor-border;
    }

    a {
      color: $ai-tutor-indigo;
      text-decoration: underline;
      word-break: break-all;
    }

    strong {
      font-weight: 800;
    }

    // Inline code (a fenced block resets these below).
    code {
      padding: 1px 5px;
      border-radius: 6px;
      background: $ai-tutor-indigo-50;
      color: $ai-tutor-ink;
      font-family: $ai-tutor-code-font;
      font-size: 0.92em;
      direction: ltr;
      unicode-bidi: isolate;
    }

    // Fenced / indented code: scrolls sideways inside the bubble.
    .ai-md__pre {
      max-width: 100%;
      margin: 0.6em 0;
      padding: 10px 12px;
      overflow-x: auto;
      border-radius: 10px;
      background: #f3f5fb;
      font-family: $ai-tutor-code-font;
      font-size: 11px;
      line-height: 1.7;
      text-align: left;
      white-space: pre;

      code {
        padding: 0;
        background: none;
        font-family: inherit;
        font-size: inherit;
      }
    }

    // GFM table: its own horizontal scroller, so a wide table never widens the
    // bubble (mobile).
    .ai-md__table {
      max-width: 100%;
      margin: 0.6em 0;
      overflow-x: auto;

      table {
        border-collapse: collapse;
        font-size: 11px;
      }

      th,
      td {
        // A column never squeezes below a readable width: on a phone the table
        // scrolls sideways instead of wrapping one glyph per line.
        min-width: 4.5em;
        padding: 5px 10px;
        border: 1px solid $ai-tutor-border;
      }

      // A formula in a cell stays in one piece (`Δ > 0`, not `Δ >` / `0`).
      .katex {
        white-space: nowrap;
      }

      th {
        background: $ai-tutor-indigo-50;
        color: $ai-tutor-ink;
        font-weight: 800;
      }
    }

    // ---- math (KaTeX)
    // Inline: always left-to-right and bidi-isolated, so Persian text around it
    // neither re-orders the formula nor is re-ordered by it.
    // BT-65 (P41): an inline formula is never broken across lines -- it moves
    // to the next line in one piece. Only one wider than the whole bubble
    // (`--wide`, set by `fitMath`) becomes a box of its own that scrolls
    // sideways; every other formula stays an ordinary inline run, on the text
    // baseline.
    .ai-md__math {
      direction: ltr;
      unicode-bidi: isolate;
      white-space: nowrap;
    }

    .ai-md__math--wide {
      display: inline-block;
      max-width: 100%;
      overflow-x: auto;
      overflow-y: hidden;
      vertical-align: middle;
      -webkit-overflow-scrolling: touch;
    }

    .katex {
      // STUDENT-UX-02 / UX2-04 (doc 30 §4, D-9; supersedes doc 11's 1.1em for
      // this rule only): KaTeX_Main's `=` is two hairlines (~0.04em). At the
      // 1.1em × 12px = 13.2px this used to be, Chromium at DPR 1 drew one bar
      // at ~70 % ink and the other at ~9 % -- `=` read as `_`. Measured on the
      // real bundle (doc 30 §4): 14.5–14.75px is the only small band where both
      // bars stay >= 100/255 at DPR 1 / 1.25 / 1.5 / 2; 1.21em (KaTeX's own
      // ratio) puts the 12px bubble at 14.52px. Surfaces with another base
      // size (OCR text 11px, suggestion labels) pin 14.5px explicitly in their
      // own styles -- 1.21em of 11px would land back in the bad band.
      font-size: 1.21em;
    }

    // Display: a block that scrolls sideways when the equation is wider than
    // the bubble.
    .ai-md__math-block {
      display: block;
      margin: 0.5em 0;
      max-width: 100%;
      overflow-x: auto;
      overflow-y: hidden;
      direction: ltr;

      .katex-display {
        margin: 0.2em 0;
        // Nothing may break inside a display equation: it scrolls instead.
        > .katex {
          white-space: nowrap;
        }
      }
    }

    .ai-md__math-eqno {
      float: right;
      color: $ai-tutor-muted;
      font-size: 0.9em;
    }

    // A formula KaTeX could not typeset: the source, in red, still readable.
    .ai-md__math-error {
      color: #b3261e;
      font-family: $ai-tutor-code-font;
      direction: ltr;
      unicode-bidi: isolate;
    }

    // AT-API-020-H3: a citation marker -- the reply's pointer at one of its
    // sources, a small raised number in the bubble's accent colour. Raised
    // from the baseline by half its own size (not `vertical-align: super`,
    // which lifts the number out of its line box into the line above) and
    // with `line-height: 0`, so a line with markers is exactly as tall as one
    // without. Two markers side by side (`[^1][^2]`) keep a small gap.
    .ai-md__cite {
      position: relative;
      top: -0.5em;
      margin-inline-start: 1px;
      color: $ai-tutor-indigo;
      font-size: 0.72em;
      font-weight: 700;
      line-height: 0;
      vertical-align: baseline;

      + .ai-md__cite {
        margin-inline-start: 3px;
      }
    }

    // POST-UAT-UX / PUX-A: the marker of a source with a page image -- the
    // same raised number, made a button. The visible glyph keeps the H3 size
    // and place; the hit area grows only VERTICALLY (24px tall, 1px beyond
    // the glyph at each side) without moving the line: wider, it would
    // reach over the next marker of `[^1][^2]` (3px away) and open the wrong
    // page -- an inline target in a sentence (WCAG 2.5.8 inline exception).
    // Underlined so it reads as something to open.
    .ai-md__cite-open {
      position: relative;
      display: inline;
      padding: 0;
      border: 0;
      background: none;
      color: inherit;
      font: inherit;
      line-height: inherit;
      text-decoration: underline;
      text-decoration-thickness: 1px;
      text-underline-offset: 2px;
      cursor: zoom-in;

      &::after {
        content: '';
        position: absolute;
        top: 50%;
        right: -1px;
        left: -1px;
        height: 24px;
        transform: translateY(-50%);
      }

      &:hover {
        color: $borna-indigo-900;
      }

      &:focus-visible {
        border-radius: 3px;
        outline: 2px solid $ai-tutor-indigo;
        outline-offset: 2px;
      }
    }
  }
}

// CHAT-UX-01 (doc 29 §4.2) -- a student's own message, on the indigo-50
// student bubble. Plain prose must look as it did as literal text: a blank
// line between two paragraphs is one empty line of the bubble (1.9), not the
// tutor reply's tighter paragraph gap; and the tutor's inline-code and
// code-block tints, which are the bubble's own colour here, become white.
.ai-md--student {
  ::v-deep {
    p {
      margin: 0;
    }

    p + p {
      margin-top: 1.9em;
    }

    code {
      background: #fff;
    }

    .ai-md__pre {
      background: #fff;

      code {
        background: none;
      }
    }

    .ai-md__table th {
      background: $ai-tutor-indigo-100;
    }
  }
}

// CHAT-UX-01 delta (D-CX-9): one line inside a suggestion card's label. The
// span flows with the card's own type; a formula in it keeps the inline
// rules above (left-to-right, bidi-isolated) and may wrap between its groups.
.ai-md--inline {
  display: inline;
  overflow-wrap: anywhere;

  // BT-65 (P41): the label keeps its formulas breakable as before -- the card
  // wraps its label, it never scrolls.
  ::v-deep .ai-md__math {
    white-space: normal;
  }
}
</style>
